import {
  INSPECTION_APPLIES_TO,
  INSPECTION_PRESETS,
  type InspectionAppliesTo,
} from '@sitewise/shared';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  Label,
  Skeleton,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCheck2, Plus, Sparkles } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import {
  createInspectionTemplate,
  listInspectionTemplates,
  seedInspectionPresets,
  type InspectionTemplateSummary,
} from '../../../api/inspection-templates.js';
import { ApiError } from '../../../lib/api-client.js';

import { BulkStartSheet } from './bulk-start-sheet.js';
import { appliesToLabel, formatDate, TemplateStatusBadge } from './lib.js';

export function InspectionTemplatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [bulkFor, setBulkFor] = useState<InspectionTemplateSummary | null>(null);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const isAdmin = meQuery.data?.user.role === 'admin';

  const tplQuery = useQuery({
    queryKey: ['inspection-templates', 'list'],
    queryFn: () => listInspectionTemplates({}),
  });

  const seedMut = useMutation({
    mutationFn: () => seedInspectionPresets(),
    onSuccess: (r) => {
      toast.success(
        r.inserted > 0 ? `Seeded ${r.inserted} presets.` : 'Presets already seeded.',
      );
      void queryClient.invalidateQueries({ queryKey: ['inspection-templates'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not seed presets');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-fg-muted">
          Templates define what to ask during an inspection. Publish a draft to make it runnable.
        </p>
        <div className="ml-auto flex gap-2">
          {isAdmin ? (
            <Button
              variant="outline"
              onClick={() => seedMut.mutate()}
              disabled={seedMut.isPending}
            >
              <Sparkles className="h-4 w-4" />
              {seedMut.isPending ? 'Seeding…' : 'Seed default presets'}
            </Button>
          ) : null}
          {isAdmin ? (
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" />
              New template
            </Button>
          ) : null}
        </div>
      </div>

      {tplQuery.isError ? (
        <ErrorState description="Could not load templates." />
      ) : tplQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (tplQuery.data?.templates.length ?? 0) === 0 ? (
        <EmptyState
          icon={FileCheck2}
          title="No inspection templates yet"
          description="Seed the default presets (DHS unit inspection, fire safety, common-area cleanliness, move-out) or create one from scratch."
          action={
            isAdmin ? (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => seedMut.mutate()}>
                  Seed default presets
                </Button>
                <Button onClick={() => setShowCreate(true)}>New template</Button>
              </div>
            ) : null
          }
        />
      ) : (
        <DataTable<InspectionTemplateSummary>
          rowKey={(r) => r.id}
          rows={tplQuery.data!.templates}
          onRowClick={(r) => navigate(`/inspections/templates/${r.id}`)}
          columns={[
            {
              id: 'name',
              header: 'Name',
              description: 'The inspection form name.',
              cell: (r) => (
                <div className="min-w-0">
                  <Link
                    to={`/inspections/templates/${r.id}`}
                    className="block truncate font-medium hover:underline"
                  >
                    {r.name}
                  </Link>
                  {r.description ? (
                    <div className="truncate text-xs text-fg-muted">{r.description}</div>
                  ) : null}
                </div>
              ),
            },
            {
              id: 'appliesTo',
              header: 'Applies to',
              description: 'Whether the template is filled out for a unit or a building.',
              cell: (r) => <span className="text-sm">{appliesToLabel(r.appliesTo)}</span>,
            },
            {
              id: 'version',
              header: 'Version',
              description: 'The current draft version, and the version that is published.',
              cell: (r) => (
                <div className="text-xs">
                  <div>v{r.currentVersion ?? '—'}</div>
                  <div className="text-fg-muted">
                    {r.publishedVersion ? `published v${r.publishedVersion}` : 'not published'}
                  </div>
                </div>
              ),
            },
            {
              id: 'status',
              header: 'Status',
              description: 'Whether the template is a draft, published, or archived.',
              cell: (r) => <TemplateStatusBadge status={r.status} />,
            },
            {
              id: 'updatedAt',
              header: 'Updated',
              description: 'When the template was last changed.',
              cell: (r) => (
                <span className="whitespace-nowrap text-xs text-fg-muted tabular-nums">
                  {formatDate(r.updatedAt)}
                </span>
              ),
            },
            {
              id: 'actions',
              header: '',
              cell: (r) =>
                r.publishedVersion && r.appliesTo === 'unit' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBulkFor(r);
                    }}
                  >
                    Bulk start
                  </Button>
                ) : null,
            },
          ]}
        />
      )}

      {showCreate ? (
        <CreateTemplateSheet onClose={() => setShowCreate(false)} />
      ) : null}

      {bulkFor ? (
        <BulkStartSheet
          open={!!bulkFor}
          onOpenChange={(o) => {
            if (!o) setBulkFor(null);
          }}
          templateId={bulkFor.id}
          templateName={bulkFor.name}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: ['inspections'] });
          }}
        />
      ) : null}
    </div>
  );
}

function CreateTemplateSheet({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const nameId = useId();
  const descId = useId();
  const appliesId = useId();
  const presetId = useId();

  const [form, setForm] = useState({
    name: '',
    description: '',
    appliesTo: 'unit' as InspectionAppliesTo,
    fromPreset: '',
  });
  const [error, setError] = useState<string | null>(null);

  const presetOptions = useMemo(
    () =>
      INSPECTION_PRESETS.filter((p) => p.appliesTo === form.appliesTo).map((p) => ({
        slug: p.slug,
        name: p.name,
      })),
    [form.appliesTo],
  );

  const create = useMutation({
    mutationFn: () =>
      createInspectionTemplate({
        name: form.name.trim(),
        description: form.description.trim() || null,
        appliesTo: form.appliesTo,
        fromPreset: form.fromPreset || undefined,
        metadata: {},
      }),
    onSuccess: (data) => {
      toast.success('Template created.');
      void queryClient.invalidateQueries({ queryKey: ['inspection-templates'] });
      navigate(`/inspections/templates/${data.template.id}`);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not create template');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate();
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex flex-col">
        <SheetHeader>
          <SheetTitle>New inspection template</SheetTitle>
          <SheetDescription>
            Drafts are private until you publish them. You can clone a preset's structure to skip
            the blank-page problem.
          </SheetDescription>
        </SheetHeader>

        <form className="flex flex-1 flex-col gap-3 overflow-y-auto" onSubmit={handleSubmit}>
          <div className="grid gap-1.5">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              required
              maxLength={200}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Move-out inspection"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={appliesId}>Applies to</Label>
            <select
              id={appliesId}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.appliesTo}
              onChange={(e) =>
                setForm({
                  ...form,
                  appliesTo: e.target.value as InspectionAppliesTo,
                  fromPreset: '',
                })
              }
            >
              {INSPECTION_APPLIES_TO.map((a) => (
                <option key={a} value={a}>
                  {appliesToLabel(a)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={presetId}>Clone from preset</Label>
            <select
              id={presetId}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.fromPreset}
              onChange={(e) => setForm({ ...form, fromPreset: e.target.value })}
            >
              <option value="">— start blank —</option>
              {presetOptions.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={descId}>Description</Label>
            <textarea
              id={descId}
              rows={3}
              maxLength={2_000}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          {error ? (
            <div className="rounded border border-danger-subtle bg-danger-subtle/40 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <SheetFooter className="mt-auto">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !form.name.trim()}>
              {create.isPending ? 'Creating…' : 'Create template'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
